import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { getAuthState } from 'commons/fleet/getHTMLInjectedValues';
import { useExtensionV3Status } from 'dashboard/containers/ExtensionStateProvider/useExtensionV3Status';

export interface ExtensionState {
  hasInstalledChromeExtension: boolean;
  extensionVersion?: string;
}

export const ExtensionStateContext = React.createContext<ExtensionState>({
  hasInstalledChromeExtension: false,
});

interface ExtensionStateProviderProps {
  children: ReactNode;
}

export const ExtensionStateProvider = ({ children }: ExtensionStateProviderProps) => {
  const initialAuthState = getAuthState();

  const v2StateRef = useRef<ExtensionState>({
    extensionVersion: initialAuthState.extensionVersion,
    hasInstalledChromeExtension: initialAuthState.hasInstalledChromeExtension,
  });

  const { extensionV3State } = useExtensionV3Status();

  const [extensionState, setExtensionState] = useState<ExtensionState>(
    extensionV3State || v2StateRef.current,
  );

  useEffect(() => {
    document.addEventListener(`extensionInstalledSuccess`, (event: CustomEvent) => {
      setExtensionState({
        extensionVersion: event.detail.version,
        hasInstalledChromeExtension: true,
      });

      if (parseFloat(event.detail.version) < 5) {
        v2StateRef.current = {
          extensionVersion: event.detail.version,
          hasInstalledChromeExtension: true,
        };
      }
    });
  }, []);

  useEffect(() => {
    setExtensionState(extensionV3State || v2StateRef.current);
  }, [extensionV3State, v2StateRef.current]);

  return (
    <ExtensionStateContext.Provider value={extensionState}>
      {children}
    </ExtensionStateContext.Provider>
  );
};

export const useExtensionInstallationState = () => useContext(ExtensionStateContext);
